import { jsxs, jsx } from 'react/jsx-runtime';
import { IconCheckboxUnselected, IconCheckboxSelected } from '@belong/icons';
import { Root, Indicator } from '@radix-ui/react-checkbox';
import { Label } from '@radix-ui/react-label';
import clsx from 'clsx';
import { Text } from '../Text/Text.js';
import { SIZE_MAP, UNCHECKED_CLASS_NAMES, CHECKED_CLASS_NAMES } from './Checkbox.constants.js';
import styles from './Checkbox.module.css.js';

function Checkbox({
  checked,
  id,
  label,
  inputContainerClass,
  name,
  onChange,
  required,
  value,
  disabled = false,
  variant = 'primary',
  size = 'default',
  fontWeight = 'semibold',
  wrapperClassName,
  labelClassName,
  labelTextClassName,
  unselectedClassName,
  hasError
}) {
  /**
   * Radix's `Label` is a `<span role="label">` that re-implements label
   * activation in JS: it forwards every click inside it to the control, without
   * the carve-out a real `<label>` has for interactive descendants. Stop the
   * click here — after the link/button has handled it — so a rich `label` can
   * hold links without a click on one also toggling the checkbox.
   */
  function handleLabelClick(event) {
    const interactive = event.target.closest('a, button');
    if (interactive && event.currentTarget.contains(interactive)) {
      event.stopPropagation();
    }
  }
  return jsxs("div", {
    className: clsx('group flex items-center hover:cursor-pointer', styles.wrapper, {
      'pointer-events-none outline-light-gray opacity-30': disabled
    }, wrapperClassName),
    children: [jsx("div", {
      className: clsx('mr-xs', inputContainerClass),
      children: jsx(Root, {
        checked: checked,
        className: clsx('flex items-center justify-center group-hover:cursor-pointer', SIZE_MAP[size], {
          'focus:outline-navy': checked,
          'focus:outline-dark-gray': !checked
        }),
        id: id,
        name: name,
        onCheckedChange: onChange,
        required: required,
        value: value,
        disabled: disabled,
        children: jsxs("div", {
          className: SIZE_MAP[size],
          children: [!checked && jsx(IconCheckboxUnselected, {
            className: clsx(SIZE_MAP[size], unselectedClassName, hasError ? 'text-red group-hover:text-red' : UNCHECKED_CLASS_NAMES[variant])
          }), jsx(Indicator, {
            className: SIZE_MAP[size],
            children: jsx(IconCheckboxSelected, {
              className: clsx(CHECKED_CLASS_NAMES[variant], SIZE_MAP[size])
            })
          })]
        })
      })
    }), jsx(Label, {
      htmlFor: id,
      className: clsx(labelClassName),
      children: jsx(Text, {
        onClick: handleLabelClick,
        className: clsx('m-0', checked ? CHECKED_CLASS_NAMES[variant] : UNCHECKED_CLASS_NAMES[variant], labelTextClassName, hasError && 'text-red transition-colors duration-hover group-hover:text-red'),
        fontWeight: fontWeight,
        variant: size === 'small' ? 'p1' : 'body',
        children: label
      })
    })]
  });
}

export { Checkbox };
